import { Box, Button, Paper, Stack, Typography } from "@mui/material"
import dayjs from "dayjs"
import React, { useContext } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { useJobContext } from "../contexts/JobContext"
import ShiftContext from "../contexts/ShiftContext"
import SiteContext from "../contexts/SiteContext"
import { jobInDeliveryPhase, jobInPickupPhase } from "../entities/job/helper"
import { EJobStatus } from "../entities/job/interface"
import { IShift } from "../entities/users/interface"
import { UserContext } from "../contexts/UserContext"
import { UPDATE_SHIFT_STATUS_MUTATION } from "../entities/shifts/graphql/mutations"
import { useMutation } from "@apollo/client"

const SelectShift = ({
    shift,
    isTodayShift,
    shiftStatus,
}: {
    shift: IShift
    isTodayShift: boolean
    shiftStatus: string
}) => {
    const { t } = useTranslation()
    const userContext = useContext(UserContext)
    const siteContext = useContext(SiteContext)
    const shiftContext = useContext(ShiftContext)
    const jobContext = useJobContext()

    const [updateShiftStatus] = useMutation(UPDATE_SHIFT_STATUS_MUTATION)

    const navigate = useNavigate() // Hook to  navigate
    if (!shift) return null // Handle edge case for missing shift data

    if (!userContext) return null // Handle edge case for missing user data
    const shiftInProgress =
        !!shiftContext?.shiftId &&
        !!jobContext.currentJobs &&
        jobContext.currentJobs[0]?.jobStatus !== EJobStatus.idle &&
        jobContext.currentJobs[0]?.jobStatus !== EJobStatus.completed
    const thisShiftInProgress = shiftContext?.selectedShift?._id === shift._id

    const updatingShiftStatus = async (
        shiftStatus: string,
        shiftId: string | number | undefined,
        driverID: string,
        vehicleID: string
    ) => {
        try {
            await updateShiftStatus({
                variables: {
                    id: shiftId,
                    record: { status: shiftStatus, driverId: driverID, vehicleId: vehicleID },
                },
            })
        } catch (error) {
            console.error("Error updating shift status:", error)
        }
    }

    const handleStartShift = async (shift: IShift) => {
        if (shiftInProgress) {
            //redirect to the Vehicle page
            const job = jobContext?.currentJobs ? jobContext.currentJobs[0] : null
            if (!job) {
                navigate(`/vehicle`, { state: { odometer: true } })
                return
            }

            const { jobStatus } = job

            if (jobStatus === EJobStatus.idle || jobStatus === EJobStatus.completed) {
                navigate(`/job-listing`)
            } else if (jobInPickupPhase(jobStatus) || jobInDeliveryPhase(jobStatus)) {
                navigate(`/job`)
            }
            return
        }

        const lastShiftStatus =
            Array.isArray(shift.shiftHistory) && shift.shiftHistory.length > 0
                ? shift.shiftHistory[shift.shiftHistory.length - 1].status
                : ""

        if (shiftContext && siteContext) {
            userContext?.user?._id &&
                lastShiftStatus === "IDLE" &&
                (await updatingShiftStatus("active", shift._id, userContext.user._id, shift.vehicle._id))

            await siteContext.setSiteId(shift.siteId)
            await shiftContext.setSelectedShift(shift)
        }

        //redirect to the Vehicle page
        navigate(`/vehicle`, { state: { odometer: true } })
    }

    return (
        <Paper
            elevation={3} // Adds shadow
            sx={{
                width: "80%",
                padding: 3,
                marginBottom: 4,
                borderRadius: 4,
                bgcolor: "primary.main", // Background color from the theme
            }}
        >
            <Stack spacing={2}>
                {/* Shift Name */}
                <Box
                    sx={{
                        alignItems: "center", // Aligns text vertically in the center
                    }}
                >
                    {/* Left Aligned Text */}
                    <Typography
                        variant="h6"
                        color="text.primary"
                        fontWeight="bold"
                        sx={{
                            alignItems: "center", // Aligns text vertically in the center
                        }}
                    >
                        {shift.shiftTemplateName}
                    </Typography>
                </Box>

                {/* Shift Details */}

                <Box display="flex" justifyContent="space-between">
                    <Typography color="text.secondary"> {t("sites.formLabels.siteName")}:</Typography>
                    <Typography color="text.primary">{shift.siteName || t("common.notAvailable")}</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between">
                    <Typography color="text.secondary"> {t("shifts.mobile.details.vehicleIdentifier")}:</Typography>
                    <Typography color="text.primary">{shift?.vehicle?.identifier}</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between">
                    <Typography color="text.secondary"> {t("shifts.formLabels.startTime")}:</Typography>
                    <Typography color="text.primary">
                        {shift.shiftStart ? dayjs(shift.shiftStart).format("DD-MMM hh:mm a") : t("common.notAvailable")}
                    </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between">
                    <Typography color="text.secondary"> {t("shifts.formLabels.endTime")}:</Typography>
                    <Typography color="text.primary">
                        {shift.shiftEnd ? dayjs(shift.shiftEnd).format("DD-MMM hh:mm a") : t("common.notAvailable")}
                    </Typography>
                </Box>

                {/* Start Shift Button */}
                <Button
                    color="secondary"
                    variant="contained"
                    fullWidth
                    onClick={async () => await handleStartShift(shift)} // Replace with your actual handler
                    disabled={!isTodayShift || shiftStatus === "COMPLETED" || (shiftInProgress && !thisShiftInProgress)} // Disable button if shift is in progress
                >
                    {t(`shifts.mobile.buttons.${shiftInProgress && thisShiftInProgress ? "continue" : "start"}`)}
                </Button>
            </Stack>
        </Paper>
    )
}

export default SelectShift
